import { useEffect } from "react";

// Per-page head tags for the SPA routes: title, description, canonical and the
// Open Graph / Twitter mirrors. index.html carries the home page's defaults;
// each route overwrites them on mount so a shared link or a crawler that runs
// JS sees this page's own metadata.
function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta({
  title,
  description,
  path,
  noindex = false,
}: {
  title: string;
  description: string;
  path: string;
  /** Private, per-person pages: add robots noindex,nofollow while mounted. */
  noindex?: boolean;
}) {
  useEffect(() => {
    const url = `${window.location.origin}${path}`;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;
  }, [title, description, path]);

  // Only touches robots when asked, and removes exactly the tag it added on
  // unmount, so every other page keeps the index.html default.
  useEffect(() => {
    if (!noindex) return;
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex,nofollow");
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [noindex]);
}
